import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Route,
  Redirect,
  Switch,
  RouteProps,
  useHistory,
  useLocation
} from 'react-router-dom';
import {
  Auth,
  Home,
  Logout,
  MailBoxes,
  ReportingOld,
  ReportingCrs,
  Teams,
  Users,
  AnonymousView,
  LodgeEnquiry,
  Upgrade,
  Onboarding,
  Settings,
  Welcome,
  Eula,
  SysAdminOrg,
  SysAdminFreePlan
} from 'modules';
import AuthRoute from './AuthRoute';
import { organisationSubscriptionExpiringDayThreshold } from 'app/contants';
import { APP_URL, SCREEN_SIZE, SCREEN_BREAK_POINT, USERSROLE } from 'types';
import { selectTheme } from 'redux/app/selectors';
import {
  selectActivateIdle,
  selectScreenSize,
  selectInitial,
  selectIsAuthenticated,
  currentUser,
  selectMaintenance
} from 'redux/session/selectors';
import {
  addNotification,
  setActivateIdle,
  setScreenSize,
  setSysAdminMode,
  fetchUserStart,
  addAlert,
  removeAllAlerts,
  mailboxUnmatchedCountStart,
  showOrganisationSuspendedModal
} from 'redux/session/actions';
import { fetchUserOrganisationStart } from 'redux/organisation/actions';
import { currentUserOrganisation } from 'redux/organisation/selectors';
import { disableTeamPicker } from 'redux/team/actions';
import { getDaysUntil } from 'utilities/helper.util';
import { useComponentMount, useEventListener } from 'utilities/hooks.util';
import { Loading } from 'carbon-components-react';
import { useIdleTimer } from 'react-idle-timer';
import { logPageView } from './AnalyticsTracker';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import Maintenance from 'modules/maintenance';
dayjs.extend(timezone);

const { MAX, XL, LG, MD } = SCREEN_BREAK_POINT;
const ALL_USERS = [
  USERSROLE.MANAGER,
  USERSROLE.ORGADMIN,
  USERSROLE.SYSADMIN,
  USERSROLE.TRIALUSER,
  USERSROLE.USER
];
const ADMIN_USERS = [USERSROLE.MANAGER, USERSROLE.ORGADMIN, USERSROLE.SYSADMIN];
const ALL_USERS_NO_TRIAL = [
  USERSROLE.MANAGER,
  USERSROLE.ORGADMIN,
  USERSROLE.SYSADMIN,
  USERSROLE.USER
];

export const APP = 'app';
const AppRoutes: React.FC<RouteProps> = () => {
  const [showAlertEmail, setShowAlertEmail] = useState<boolean>(true);
  const [showAlertLinking, setShowAlertLinking] = useState<boolean>(true);
  const dispatch: any = useDispatch();
  const activateIdle = useSelector(selectActivateIdle);
  const appTheme = useSelector(selectTheme);
  const screenSize = useSelector(selectScreenSize);
  const seesionIntialed = useSelector(selectInitial);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isMaintenance = useSelector(selectMaintenance);
  const user = useSelector(currentUser);
  const organisation = useSelector(currentUserOrganisation);
  const location = useLocation();
  const history = useHistory();

  const { start: startIdleTimer } = useIdleTimer({
    timeout: Number(process.env.REACT_APP_IDLE_TIME) || 900000,
    onIdle: () => history.push(APP_URL.LOGOUT)
  });

  const handleResize = () => {
    const height = document.documentElement?.clientHeight || window.innerHeight;
    const width = window.innerWidth;
    document.documentElement.style.setProperty(
      '--app-height',
      `${height - 1}px`
    );
    if (width >= MAX) {
      dispatch(setScreenSize(SCREEN_SIZE.MAX));
    } else if (width < MAX && width >= XL) {
      dispatch(setScreenSize(SCREEN_SIZE.XL));
    } else if (width < XL && width > LG) {
      dispatch(setScreenSize(SCREEN_SIZE.LG));
    } else if (width <= LG && width >= MD) {
      dispatch(setScreenSize(SCREEN_SIZE.MD));
    } else {
      dispatch(setScreenSize(SCREEN_SIZE.SM));
    }
  };

  useEventListener('resize', handleResize, window);

  useEffect(() => {
    if (isAuthenticated) {
      if (!user.is_linked) {
        const title = 'Warning';
        const details =
          'SmarterWX detected that your BYDA account has not been linked';
        showAlertLinking &&
          dispatch(addAlert('warning', title, details, true, 0));
        setShowAlertLinking(false);
      } else if (user.is_linked && !user.is_email_linked) {
        const title = 'Warning';
        const details =
          'SmarterWX detected that your BYDA contact email was changed';
        showAlertEmail &&
          dispatch(addAlert('warning', title, details, true, 0));
        setShowAlertEmail(false);
      } else {
        dispatch(removeAllAlerts(true));
      }
      dayjs.tz.setDefault(user?.timezone || 'Australia/Melbourne');

      // check for suspended organisation
      if (
        user.Organisation?.is_inactive ||
        (user.is_as_external_user && user.Projects.length === 0)
      ) {
        dispatch(showOrganisationSuspendedModal(true));
      }
    }
  }, [isAuthenticated, user, dispatch, showAlertLinking, showAlertEmail]);

  useEffect(() => {
    const subscriptionEndDate = organisation?.contract_renewal;
    if (subscriptionEndDate) {
      const remainingDays = getDaysUntil(subscriptionEndDate, true);
      if (
        remainingDays >= 0 &&
        remainingDays <= organisationSubscriptionExpiringDayThreshold
      ) {
        dispatch(
          addNotification('organisationSubscriptionExpiring', {
            remainingDays
          })
        );
      }
    }
  }, [dispatch, organisation]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUserOrganisationStart());
      dispatch(mailboxUnmatchedCountStart());
      history.listen(() => {
        dispatch(mailboxUnmatchedCountStart());
      });
    }
  }, [isAuthenticated, history, dispatch]);

  useEffect(() => {
    if (activateIdle) {
      startIdleTimer();
      dispatch(setActivateIdle(false));
    }
  }, [activateIdle, dispatch, startIdleTimer]);

  useEffect(() => {
    const enabledTeamPickerPages: string[] = [
      APP_URL.NEW_ENQUIRY,
      APP_URL.ROOT_URL
    ];
    dispatch(
      disableTeamPicker(!enabledTeamPickerPages.includes(location.pathname))
    );
  }, [location, dispatch]);

  useEffect(() => {
    logPageView(history);
  }, [history]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isSys = queryParams.get('isSys');
    if (!!isSys && !!user?.role_type) {
      dispatch(setSysAdminMode(user?.role_type === USERSROLE.SYSADMIN));
      queryParams.delete('isSys');
      history.replace({ search: queryParams.toString() });
    }
  }, [user, location, dispatch, history]);

  useComponentMount(() => {
    location.pathname !== APP_URL.AUTH && dispatch(fetchUserStart());
    handleResize();
  });

  return (
    <div
      className={`app --swx-theme--${appTheme}${
        screenSize === SCREEN_SIZE.SM ? ' --swx-typography--sm' : ''
      }`}
      data-testid="app"
      id="app"
    >
      {seesionIntialed || location.pathname === APP_URL.AUTH ? (
        <>
          {isMaintenance === false ? (
            <Switch>
              <Route
                key="Auth"
                exact={true}
                path={APP_URL.AUTH}
                render={(props: any) => <Auth {...props} />}
              />
              <Route
                key="Welcome"
                exact={true}
                path={APP_URL.WELCOME}
                render={(props: any) => <Welcome {...props} />}
              />

              <AuthRoute
                key="LodgeEnquiry"
                path={APP_URL.NEW_ENQUIRY}
                exact={true}
                page={LodgeEnquiry}
                requiredRole={ALL_USERS_NO_TRIAL}
              />
              <Route
                key="AnonymousView"
                exact={false}
                path={`${APP_URL.ANONYMOUS_VIEW}`}
                render={(props: any) => <AnonymousView {...props} />}
              />
              <AuthRoute
                key="Home"
                path={APP_URL.ROOT_URL}
                exact={true}
                page={Home}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="Logout"
                path={APP_URL.LOGOUT}
                exact={true}
                page={Logout}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="MailBoxes"
                path={APP_URL.MAIL_BOXES}
                exact={true}
                page={MailBoxes}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="ReportingOld"
                path={APP_URL.REPORTING_OLD}
                exact={true}
                page={ReportingOld}
                requiredRole={ADMIN_USERS}
              />
              <AuthRoute
                key="ReportingCrs"
                path={APP_URL.REPORTING}
                exact={true}
                page={ReportingCrs}
                requiredRole={ADMIN_USERS}
              />
              <AuthRoute
                key="Teams"
                path={APP_URL.TEAMS}
                exact={true}
                page={Teams}
                requiredRole={ALL_USERS_NO_TRIAL}
              />
              <AuthRoute
                key="Users"
                path={APP_URL.USERS}
                exact={true}
                page={Users}
                requiredRole={ADMIN_USERS}
              />
              <AuthRoute
                key="Upgrade"
                path={APP_URL.UPGRADE}
                exact={true}
                page={Upgrade}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="Onboarding"
                path={APP_URL.ONBOARDING}
                exact={true}
                page={Onboarding}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="Settings"
                path={APP_URL.SETTINGS}
                exact={true}
                page={Settings}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="Eula"
                path={APP_URL.EULA}
                exact={true}
                page={Eula}
                requiredRole={ALL_USERS}
              />
              <AuthRoute
                key="SysAdminOrg"
                path={`${APP_URL.SYSADMIN_ORG}/:orgId?`}
                exact={true}
                page={SysAdminOrg}
                requiredRole={[USERSROLE.SYSADMIN]}
              />
              <AuthRoute
                key="SysAdminFreePlan"
                path={APP_URL.SYSADMIN_FREE_PLAN}
                exact={true}
                page={SysAdminFreePlan}
                requiredRole={[USERSROLE.SYSADMIN]}
              />
              <Route path="*">
                <Redirect
                  to={{
                    pathname: APP_URL.AUTH,
                    state: { from: `${location.pathname}${location.search}` }
                  }}
                />
              </Route>
            </Switch>
          ) : (
            <Maintenance />
          )}
        </>
      ) : (
        <Loading withOverlay={true} />
      )}
    </div>
  );
};

AppRoutes.displayName = 'RootApp';
export default AppRoutes;
